/**
 * Ce que la fiche candidat sait des épreuves d'un parcours : leur clé d'onglet,
 * leur libellé, et lesquelles portent un matching.
 *
 * Les onglets suivent les ÉPREUVES DU PARCOURS, pas les tests de la campagne :
 * un parcours hors campagne peut porter un autodiagnostic ou un égogramme, qui
 * n'ont pas d'onglet de campagne.
 */
import { useFiltresUrl } from "@repo/ui";
import { getEvaluationTitle } from "@repo/utils";
import type { Id } from "../../../../convex/_generated/dataModel";

/** Une épreuve du parcours, telle que projetée par `evaluationsCache`. */
export type EpreuveFiche = {
  evalId: Id<"evaluations">;
  typeEval: string;
  template?: string;
  statut: string;
  dateDebut?: number;
  dateFin?: number;
};

/**
 * Clé d'onglet d'une épreuve : le template pour un questionnaire (pami, pc-fit,
 * copsoq3…), le type sinon (inbasket, fairview). Lisible dans l'URL.
 */
export function cleEpreuve(e: { typeEval: string; template?: string }): string {
  return e.typeEval === "form" && e.template ? e.template : e.typeEval;
}

/** Matching porté par une épreuve, et contre quoi elle se compare. */
export type GenreMatching =
  | { kind: "referentiel"; cible: "pami" | "pc-fit" }
  | { kind: "bareme" };

/**
 * PAMI et PC Fit se comparent au référentiel de la campagne, FairView au barème
 * de son template. Les autres épreuves n'ont pas de matching.
 */
export function matchingDeLEpreuve(cle: string): GenreMatching | null {
  if (cle === "pami") return { kind: "referentiel", cible: "pami" };
  if (cle === "pc-fit") return { kind: "referentiel", cible: "pc-fit" };
  if (cle === "fairview") return { kind: "bareme" };
  return null;
}

export function libelleEpreuveFiche(e: { typeEval: string; template?: string }) {
  return getEvaluationTitle(e);
}

export function descriptionEpreuve(e: {
  typeEval: string;
  template?: string;
}): string {
  if (e.typeEval === "form") {
    switch (e.template) {
      case "autodiag":
        return "Questionnaire d'auto-évaluation des compétences";
      case "egogramme":
        return "Questionnaire sur le style relationnel";
      case "pami":
        return "Questionnaire de personnalité";
      case "pc-fit":
        return "Classement Q-sort des priorités culturelles";
      case "copsoq3":
        return "Questionnaire sur les conditions de travail";
      default:
        return "Questionnaire";
    }
  }
  if (e.typeEval === "fairview") return e.template || "Entretien structuré";
  return "Gestion de boîte de réception simulée";
}

/** Épreuves affichées : le conteneur technique `resultats` n'en est pas une. */
export function epreuvesAffichees<T extends { typeEval: string }>(
  epreuves: readonly T[],
): T[] {
  return epreuves.filter((e) => e.typeEval !== "resultats");
}

export type VueEpreuve = "resultats" | "matching";

/**
 * Référence stable : recréée à chaque rendu, elle ferait boucler les mémos de
 * `useFiltresUrl`.
 */
const DEFAUTS: { fiche: string; vue: string } = {
  fiche: "ensemble",
  vue: "resultats",
};

export type VueFiche = {
  /** `ensemble` ou la clé d'une épreuve. Non validé contre le parcours. */
  onglet: string;
  vue: VueEpreuve;
  choisirOnglet: (onglet: string) => void;
  choisirVue: (vue: VueEpreuve) => void;
  /** Onglet ET vue d'un coup : deux écritures d'URL successives s'écraseraient. */
  ouvrir: (onglet: string, vue: VueEpreuve) => void;
  /**
   * Le matching de cette épreuve est-il affiché ? La vue d'ensemble résume les
   * scores de toutes les épreuves, l'onglet d'une épreuve seulement le sien en
   * vue Matching.
   */
  afficheMatching: (cle: string) => boolean;
};

/**
 * Onglet et vue de la fiche, dans l'URL (`?fiche=pami&vue=matching`) : coller le
 * lien rouvre le candidat sur le même écran. Clés distinctes de celles du
 * détail de campagne (`onglet`, `section`), pour que la fiche puisse s'y ouvrir
 * en panneau sans les écraser.
 *
 * Partagé avec le portail, qui en déduit les passations de campagne à charger.
 */
export function useVueFiche(): VueFiche {
  const [params, setParams] = useFiltresUrl(DEFAUTS);
  const onglet = params.fiche;
  const vue: VueEpreuve = params.vue === "matching" ? "matching" : "resultats";
  return {
    onglet,
    vue,
    // Changer d'épreuve garde la vue : passer du matching PAMI au matching PC Fit
    // compare la même chose. L'onglet d'arrivée retombe sur Résultats s'il n'a
    // pas de matching.
    choisirOnglet: (valeur) => setParams({ fiche: valeur }),
    choisirVue: (valeur) => setParams({ vue: valeur }),
    ouvrir: (fiche, valeur) => setParams({ fiche, vue: valeur }),
    afficheMatching: (cle) =>
      onglet === "ensemble" || (onglet === cle && vue === "matching"),
  };
}
